import React, { useRef, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import SummaryDownloadButton from "../summary/PDFDownloader";
import { getHeaderClass } from "../../../utils/agGridHelpers";

const YearlySummaryPage = ({ data, type }) => {
  const gridRef = useRef(null);
  const { summary = [], OverallTotal = 0 } = data || {};

  const onGridReady = (params) => {
    gridRef.current = params.api;
  };

  // Build rows: books + yearly total + grand total
  const flattenedData = useMemo(() => {
    if (!summary.length) return [];

    const allRows = [];

    summary.forEach((yearItem) => {
      // Add all books for the year
      yearItem.books.forEach((book) => {
        allRows.push({
          year: yearItem.year,
          bookNo: book.bookNo,
          totalAmount: book.totalAmount,
          rowType: "normal",
        });
      });

      // Add Year Total row
      allRows.push({
        year: yearItem.year,
        bookNo: "Year Total",
        totalAmount: yearItem.grandTotal,
        rowType: "yearTotal",
      });
    });

    // Add Overall Total row
    allRows.push({
      year: "",
      bookNo: "Grand Total",
      totalAmount: OverallTotal,
      rowType: "grandTotal",
    });

    return allRows;
  }, [summary, OverallTotal]);

  // Column definitions with custom logic
  const columnDefs = [
    {
      headerName: "S.NO",
      valueGetter: (params) => {
        const api = params.api;
        let index = 0;
        let rowIndex = 0;
        api.forEachNodeAfterFilterAndSort((node) => {
          if (node.data?.rowType === "normal") {
            index++;
          }
          if (node === params.node) {
            if (node.data?.rowType !== "normal") {
              rowIndex = "";
            } else {
              rowIndex = index;
            }
          }
        });
        return rowIndex;
      },
      flex: 1,
    },
    {
      headerName: "Year",
      field: "year",
      valueFormatter: (params) =>
        params.data?.rowType === "yearTotal" ||
        params.data?.rowType === "grandTotal"
          ? ""
          : params.value,
      flex: 1,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Book No",
      field: "bookNo",
      flex: 1,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Total Amount",
      field: "totalAmount",
      valueFormatter: (params) =>
        params.value != null
          ? new Intl.NumberFormat("en-IN").format(params.value)
          : "",
      flex: 1,
      filter: "agTextColumnFilter",
    },
  ];

  const defaultColDef = {
    flex: 1,
    minWidth: 100,
    resizable: true,
    headerClass: getHeaderClass,
  };

  const getRowStyle = (params) => {
    if (params.data?.rowType === "yearTotal") {
      return { fontWeight: "bold", background: "#e0f7fa" };
    }
    if (params.data?.rowType === "grandTotal") {
      return { fontWeight: "bold", background: "#ffe0b2" };
    }
    return {};
  };

  return (
    <div className="ag-theme-alpine" style={{ width: "100%", height: "550px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "10px",
        }}
      >
        <SummaryDownloadButton data={data} type={type} />
      </div>
      <AgGridReact
        ref={gridRef}
        onGridReady={onGridReady}
        rowData={flattenedData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowStyle={getRowStyle}
        domLayout="autoHeight"
        pagination={true}
        paginationPageSize={10}
        paginationPageSizeSelector={[10, 20, 50]}
      />
    </div>
  );
};

export default YearlySummaryPage;
